<template>
  <div class="countries-analytic">
    <Wrapper :loading="loading">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="card-title mb-0">
            <i class="fas fa-globe mr-2"></i>
            Страны<br>
            <small style="padding-left: 26px; font-size: 11px;">Рассчитывается только для заказов с запросом на ТЭО</small>
          </h5>
          <b-form-checkbox 
            v-model="showChart" 
            switch 
            size="sm"
          >
            График
          </b-form-checkbox>
        </div>
      </div>
      <div class="card-body p-0 pt-3">
        <div class="row">
          <div :class="showChart ? 'col-md-5' : 'col-5'">
            <div class="mb-4" v-if="countryData.length > 0">
              <b-table 
                :fields="fields"
                :items="countryDataWithTotal" 
                bordered 
                striped 
                hover
                small
                thead-tr-class="all-th-black"
                :tbody-tr-class="getRowClass"
              >
                <template #cell(country_name)="data">
                  {{ data.item.country_name }}
                </template>
                <template #cell(count)="data">
                  <span 
                    v-if="!data.item.isTotal"
                    class="cursor-pointer" 
                    @click="showByIds(data.item.order_ids, 'orders', data.item.country_name)"
                  >
                    {{ data.item.count }}
                  </span>
                  <strong v-else>{{ data.item.count }}</strong>
                </template>
              </b-table>
            </div>

            <div class="mb-4" v-else-if="!loading">
              <div class="text-center text-muted">
                <i class="fas fa-globe-slash fa-3x mb-3"></i>
                <p>Нет данных для отображения</p>
              </div>
            </div>
          </div>
          
          <div class="col-md-7" v-if="showChart && countryData.length > 0">
            <div class="chart-container">
              <h6 class="text-center mb-3">Распределение по странам</h6>
              <PieChart 
                :chart-data="chartData"
                :options="chartOptions"
                :height="400"
              />
            </div>
          </div>
        </div>

        <tsoft-order-list
          v-if="orderList && orderList.length > 0"
          :openOrderList="openOrderList"
          :orderList="orderList"
          :label="orderListLabel"
          :ownFields="orderListFields"
          :withQuotationFields="['loading_places:country_id', 'transport_types_id']"
          @closeOrderList="closeOrderList"
        >
        </tsoft-order-list>
      </div>
    </Wrapper>
  </div>
</template>

<script>
import OrderList from '@/components/orders/OrderList';
import Wrapper from '@/components/wrapper/Wrapper';
import PieChart from '@/components/charts/PieChart';
import { API_SALES_ANALYTICS_COUNTRIES } from '@/constants';

export default {
  name: 'CountriesAnalytic',

  components: {
    "tsoft-order-list": OrderList,
    Wrapper,
    PieChart
  },

  props: {
    filters: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      data: {},
      fields: [
        { key: 'country_name', label: 'Страна' },
        { key: 'count', label: 'Количество', thStyle: { width: '150px' } }
      ],
      orderList: [],
      openOrderList: false,
      orderListLabel: '',
      orderListFields: [
        {
          key: 'id',
          label: 'ID',
          stickyColumn: true,
          sortable: true,
          sortDirection: 'desc',
          visible: true,
          'tdAttr': {
            'data-t': 'n',
          }
        },
        {
          key: 'company.name',
          label: 'Клиент',
          stickyColumn: false,
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        // {
        //   key: 'loading_places',
        //   label: 'Место загрузки',
        //   stickyColumn: false,
        //   sortable: true,
        //   sortDirection: 'desc',
        //   visible: true
        // },
        {
          key: 'creator.name',
          label: 'Менеджер поставки',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'sales_manager',
          label: 'Sales менеджер',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          label: "Последний комментарий",
          sortable: false,
          key: "last_comment",
        },
        {
          key: 'source.name',
          label: 'Источник поставки',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'selected_services',
          label: 'Вид услуги',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'receive_datetime',
          label: 'Дата получения заявки',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'status.name',
          label: 'Статус',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'close_reason.name',
          label: 'Причина закрытия',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'close_comment',
          label: 'Комментарий при закрытии',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        },
        {
          key: 'updated_at',
          label: 'Последние изменения в карточке',
          sortable: true,
          sortDirection: 'desc',
          visible: true
        }
      ],
      showChart: true
    };
  },

  computed: {
    countryData() {
      const result = [];
      
      // Преобразуем данные для отображения в таблице
      for (const key in this.data) {
        if (this.data[key]) {
          result.push({
            country_name: this.data[key].country_name,
            count: this.data[key].order_ids.length,
            order_ids: this.data[key].order_ids
          });
        }
      }
      
      // Сортируем по количеству (убывание), затем по имени
      return result.sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return a.country_name.localeCompare(b.country_name, 'ru');
      });
    },

    countryDataWithTotal() {
      const data = [...this.countryData];
      
      // Считаем общее количество
      const totalCount = data.reduce((sum, item) => sum + item.count, 0);
      
      // Добавляем строку "Итого"
      if (data.length > 0) {
        data.push({
          country_name: 'Итого',
          count: totalCount,
          order_ids: [],
          isTotal: true
        });
      }
      
      return data;
    },

    chartData() {
      if (this.countryData.length === 0) {
        return {};
      }

      return {
        labels: this.countryData.map(item => item.country_name),
        datasets: [{
          data: this.countryData.map(item => item.count),
          backgroundColor: this.generateColors(this.countryData.length),
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: true,
            color: 'white',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
              if (total === 0) return '';
              const percentage = ((value / total) * 100).toFixed(1);
              const label = context.chart.data.labels[context.dataIndex];
              
              // Для больших сегментов показываем процент и название
              if (percentage > 10) {
                return `${percentage}%\n${label}`;
              }
              // Для средних сегментов только процент
              else if (percentage > 5) {
                return `${percentage}%`;
              }
              // Мелкие сегменты без подписей
              return '';
            },
            anchor: 'center',
            align: 'center',
            textAlign: 'center'
          }
        },
        legend: {
          position: 'bottom',
          labels: {
            padding: 10,
            usePointStyle: true,
            fontSize: 12
          }
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((sum, value) => sum + value, 0);
              const currentValue = dataset.data[tooltipItem.index];
              const percentage = ((currentValue / total) * 100).toFixed(1);
              return `${data.labels[tooltipItem.index]}: ${currentValue} (${percentage}%)`;
            }
          }
        }
      };
    }
  },

  watch: {
    filters: {
      handler() {
        this.loadData();
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    getRowClass(item, type) {
      if (item && item.isTotal) {
        return 'table-total-row';
      }
      return '';
    },

    generateColors(count) {
      // Базовая палитра уникальных цветов
      const baseColors = [
        '#FF6B9D', // Розовый
        '#96CEB4', // Зеленый
        '#FF6384', // Красный
        '#36A2EB', // Синий
        '#FFCE56', // Желтый
        '#4BC0C0', // Бирюзовый
        '#9966FF', // Фиолетовый
        '#FF9F40', // Оранжевый
        '#45B7D1', // Голубой
        '#FFEAA7', // Светло-желтый
        '#DDA0DD', // Сливовый
        '#98D8C8', // Мятный
        '#F7DC6F', // Золотистый
        '#BB8FCE', // Лавандовый
        '#85C1E9', // Небесно-голубой
        '#F8C471'  // Персиковый
      ];

      // Если нужно больше цветов, чем в базовой палитре
      if (count <= baseColors.length) {
        return baseColors.slice(0, count);
      }

      // Генерируем дополнительные цвета
      const colors = [...baseColors];
      while (colors.length < count) {
        const hue = (colors.length * 137.508) % 360; // Золотое сечение для равномерного распределения
        const saturation = 70 + (colors.length % 3) * 10; // Варьируем насыщенность
        const lightness = 50 + (colors.length % 4) * 10; // Варьируем яркость
        colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
      }

      return colors.slice(0, count);
    },

    loadData() {
      this.$emit('update-loading', true);

      const request = {
        period_start: this.filters.period_start,
        period_end: this.filters.period_end,
        // service_ids: this.filters.service_ids,
        service_ids: [23],
        departments: this.filters.departments
      };
      
      axios
        .post(`${API_SALES_ANALYTICS_COUNTRIES}`, request)
        .then(({ data }) => {
          this.data = data.data;
          this.$emit('update-loading', false);
        })
        .catch((error) => {
          console.error('Ошибка загрузки данных по странам:', error);
          this.$emit('update-loading', false);
        });
    },

    showByIds(ids, type = 'orders', label = 'Список') {
      if (type == 'orders') {
        this.orderList = ids;
        this.orderListLabel = label;
        this.openOrderList = true;
      }
    },

    closeOrderList() {
      this.orderList = [];
      this.openOrderList = false;
    }
  },
};
</script>

<style scoped>
.countries-analytic .card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.cursor-pointer {
  cursor: pointer;
  color: #007bff;
  text-decoration: underline;
}

.cursor-pointer:hover {
  color: #0056b3;
}

.chart-container {
  position: relative;
  height: 470px;
  padding: 1rem;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
}

/* Компактные строки таблицы */
.countries-analytic >>> .table td,
.countries-analytic >>> .table th {
  padding: 0.6rem 0.6rem;
  font-size: 13px;
  vertical-align: middle;
}

.countries-analytic >>> .table {
  margin-bottom: 0;
}

.countries-analytic >>> .table-sm td,
.countries-analytic >>> .table-sm th {
  padding: 0.6rem 0.6rem;
}

/* Стили для строки "Итого" */
.countries-analytic >>> .table-total-row {
  background-color: #d9d9d9 !important;
  font-weight: bold;
}

.countries-analytic >>> .table-total-row:hover {
  background-color: #5a6268 !important;
  color: white !important;
}
</style> 